import { useState, useMemo, useEffect } from "react";
import NextLink from "next/link";
import { HiPlus } from "react-icons/hi";
import type { Card } from "@memento/types/card";
import { getPaginationInfo } from "@memento/library/pagination";

import type { CardFilter } from "src/types/card";
import { GroupVideoListItem } from "./GroupVideoListItem";
import { GroupVideosFilter } from "./GroupVideosFilter";
import { HowToCreateGroupVideo } from "./HowToCreateGroupVideo";
import { ContributeToGroupVideo } from "./ContributeToGroupVideo";
import { GroupVideoListPagination } from "./GroupVideoListPagination";
import { useAuth } from "src/hooks/useAuth";
import { useScrollRestore } from "src/hooks/useScrollRestore";
import { Button } from "@memento/ui/Button";
import { LoadingComponent } from "src/components/LoadingComponent";

type GroupVideosListProps = {
  cards: Card[];
  contributorCards: Card[];
  isLoading: boolean;
};

const PAGE_SIZE = 5;

const defaultFilterOptions = [
  { title: "All", description: "All videos", value: "all" },
  {
    title: "I'm Organizing",
    description: "Videos you're organizing",
    value: "organizing",
  },
  {
    title: "I'm Co-Organizing",
    description: "Videos you're co-organizing",
    value: "collaborating",
  },
  {
    title: "I'm Contributing To",
    description: "Videos you're contributing to",
    value: "contributing",
  },
] as const;

export function GroupVideosList({
  cards,
  contributorCards,
  isLoading,
}: GroupVideosListProps) {
  const { user } = useAuth();
  const { scrollRef, restoreScroll } = useScrollRestore<HTMLDivElement>();

  const [filter, setFilter] = useState<CardFilter>(defaultFilterOptions[0]);
  const [activePage, setActivePage] = useState(1);

  const paginationInfo = getPaginationInfo({
    totalItems: cards.length,
    perPage: PAGE_SIZE,
    requestedPage: activePage,
  });

  function sortCardsNewestToOldest(cards: Card[]) {
    // Remember that .sort() operates in-place
    return [...cards].sort((a, b) => {
      return b.createdAt - a.createdAt;
    });
  }

  const sortedCards = useMemo(() => {
    return sortCardsNewestToOldest(cards);
  }, [cards]);

  const sortedContributorCards = useMemo(() => {
    return sortCardsNewestToOldest(contributorCards);
  }, [contributorCards]);

  const cardIds = useMemo(() => {
    return sortedCards.map((card) => card.cardId);
  }, [sortedCards]);

  // if there is a duplicate video in filteredCards and contributorCards then remove it from contributorCards
  const dedupedContributorCards: Card[] = useMemo(() => {
    return sortedContributorCards.filter(
      (card) => !cardIds.includes(card.cardId)
    );
  }, [sortedContributorCards, cardIds]);

  const filteredCards = useMemo(() => {
    const sortedAllCards = sortCardsNewestToOldest([
      ...sortedCards,
      ...dedupedContributorCards,
    ]);

    return {
      all: sortedAllCards,
      organizing: sortedCards.filter(
        (card) => card.username === user?.username
      ),
      collaborating: sortedCards.filter(
        (card) => card.username !== user?.username
      ),
      contributing: sortedContributorCards,
    };
  }, [
    sortedCards,
    sortedContributorCards,
    dedupedContributorCards,
    user?.username,
  ]);

  const availableFilters = useMemo(() => {
    const available = Object.entries(filteredCards)
      .filter(([, value]) => value.length > 0)
      .map(([key]) => key);

    return defaultFilterOptions.filter((option) =>
      available.includes(option.value)
    );
  }, [filteredCards]);

  const currentCards = useMemo(() => {
    // Select the cards based on the filter, then take the first x number of
    // cards, where x is the page size (PAGE_SIZE).
    return filteredCards[filter.value].slice(
      paginationInfo.sliceStart,
      paginationInfo.sliceEnd
    );
  }, [
    filter,
    filteredCards,
    paginationInfo.sliceStart,
    paginationInfo.sliceEnd,
  ]);

  const contributingCardIdsMap = useMemo(() => {
    const result: Record<string, boolean> = {};

    for (const card of dedupedContributorCards) {
      result[card.cardId] = true;
    }

    return result;
  }, [dedupedContributorCards]);

  useEffect(() => {
    setActivePage(1);
  }, [filter]);

  function handlePageChange(pageNumber: number) {
    if (Number.isInteger(pageNumber)) {
      setActivePage(pageNumber);
      restoreScroll();
    }
  }

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <div className="flex w-full flex-col items-center">
      <div className="flex w-full items-center justify-between py-8">
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 md:space-x-4"
          ref={scrollRef}
        >
          <h1 className="text-2xl font-normal sm:text-3xl sm:font-medium">
            Group Videos
          </h1>
          {
            // Only show the filter if there are more than 2 options because if
            // there are only two options, we're showing all the GVs anyway (the
            // "all" option, which is always shown, and the other option have
            // the same list of GVs).
            availableFilters.length > 2 ? (
              <GroupVideosFilter
                selected={filter}
                onChange={setFilter}
                options={availableFilters}
              />
            ) : null
          }
        </div>
        <div>
          <NextLink href="/group-videos/new">
            <Button
              data-test="create-group-video-link"
              variant="primary"
              width="icon"
              className="rounded-full px-2 sm:rounded-md sm:px-4"
            >
              <HiPlus className="block h-6 w-6 fill-current sm:hidden" />
              <span className="hidden sm:block">Create a Group Video</span>
            </Button>
          </NextLink>
        </div>
      </div>

      <div className="grid w-full justify-items-stretch gap-8">
        {filter.value === "all" && filteredCards.all.length === 0 ? (
          <>
            <HowToCreateGroupVideo />
            <ContributeToGroupVideo />
          </>
        ) : null}
        {filter.value === "organizing" &&
        filteredCards.organizing.length === 0 ? (
          <HowToCreateGroupVideo />
        ) : null}
        {filter.value === "collaborating" &&
        filteredCards.collaborating.length === 0 ? (
          <HowToCreateGroupVideo />
        ) : null}
        {filter.value === "contributing" &&
        filteredCards.contributing.length === 0 ? (
          <ContributeToGroupVideo />
        ) : null}

        {currentCards.map(({ cardId, cardUrl }) => (
          <NextLink
            key={cardId}
            href={
              contributingCardIdsMap[cardId]
                ? `/${cardUrl}/${cardId}/record`
                : `/group-videos/${cardUrl}/${cardId}`
            }
            className="text-current hover:text-current"
          >
            <GroupVideoListItem
              cardId={cardId}
              cardUrl={cardUrl}
              isContributor={filteredCards.contributing.some(
                (contributingCard) => contributingCard.cardId === cardId
              )}
            />
          </NextLink>
        ))}
      </div>

      {filteredCards[filter.value].length > PAGE_SIZE ? (
        <div className="flex justify-center pt-16">
          <GroupVideoListPagination
            page={paginationInfo.page}
            totalPages={paginationInfo.totalPages}
            maxPagesDisplayed={3}
            onPageChange={handlePageChange}
          />
        </div>
      ) : null}
    </div>
  );
}
